/**
 * YetiChat — the conversational way to create a playbook, built to match the
 * approved prototype: a real chat shell (header + avatar, quick-reply chips, a
 * bordered composer pill, a one-line footer) rather than a plain form.
 *
 * This component owns the CONVERSATION only. On "Write the playbook" it hands
 * the full transcript up via onGenerate() and the parent runs the existing
 * outline → pages → save pipeline on it.
 *
 * Streaming brain: YETI_CHAT → POST /api/playbook/ai/plan. YETI replies in
 * plain text and may end a turn with two optional markers we parse out:
 *   [[title: A good name]]      → offer a title (fills the field if empty)
 *   [[chips: one | two | three]] → tappable quick replies
 * If the endpoint isn't reachable the chat degrades gracefully.
 */
import React, { useEffect, useRef, useState } from "react";
import { YETI_CHAT } from "../../api/playbook";

// The four-point sparkle — used on the primary action button.
const Sparkle = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.9 15.5A2 2 0 0 0 8.5 14L2.4 12.5a.5.5 0 0 1 0-1L8.5 10A2 2 0 0 0 9.9 8.5l1.6-6.1a.5.5 0 0 1 1 0L14 8.5A2 2 0 0 0 15.5 10l6.1 1.5a.5.5 0 0 1 0 1L15.5 14a2 2 0 0 0-1.4 1.5l-1.6 6.1a.5.5 0 0 1-1 0z" />
  </svg>
);

// The living YETI orb — YETI's face across the app (header + each message).
const Orb = ({ size = 26 }) => (
  <span className="pb-orb" style={{ width: size, height: size }} aria-hidden="true">
    <span className="pb-orb-s" />
  </span>
);

// YETI opens the conversation — warm, one question at a time (the Claude-chat
// feel) — and offers a few starter answers so the admin knows what to say.
const OPENING =
  "Hey — I'm YETI. I'll help you shape this playbook. To start: what's it for, " +
  "and who's going to read it?";
const OPENING_CHIPS = [
  "Onboarding for new hires",
  "A full company handbook",
  "Safety & field procedures",
];

// Pull [[title: …]] and [[chips: a | b]] markers off the end of a reply.
const parseMarkers = (raw) => {
  let text = raw;
  let suggestedTitle = null;
  let chips = [];
  const titleM = text.match(/\[\[\s*title:\s*([^\]]+?)\s*\]\]/i);
  if (titleM) {
    suggestedTitle = titleM[1].trim();
    text = text.replace(titleM[0], "");
  }
  const chipsM = text.match(/\[\[\s*chips:\s*([^\]]+?)\s*\]\]/i);
  if (chipsM) {
    chips = chipsM[1].split("|").map((c) => c.trim()).filter(Boolean).slice(0, 3);
    text = text.replace(chipsM[0], "");
  }
  let sections = null;
  const secM = text.match(/\[\[\s*sections:\s*([^\]]+?)\s*\]\]/i);
  if (secM) {
    sections = secM[1].split("|").map((s) => s.trim()).filter(Boolean).slice(0, 12);
    text = text.replace(secM[0], "");
  }
  // [[ready]] — YETI signals it understands the ask and is ready to write, so
  // the UI can light up the "Write the playbook" moment instead of the admin
  // wondering how many more questions are coming.
  let ready = false;
  const readyM = text.match(/\[\[\s*ready\s*\]\]/i);
  if (readyM) {
    ready = true;
    text = text.replace(readyM[0], "");
  }
  return { text: text.trim(), suggestedTitle, chips, sections, ready };
};

// Turn the whole exchange into one prompt for the generator. The admin's
// answers carry the real signal, so they lead; YETI's questions give context.
const transcriptOf = (msgs) =>
  msgs
    .map((m) => `${m.role === "user" ? "Admin" : "YETI"}: ${m.content.trim()}`)
    .join("\n\n");

export default function YetiChat({ title, setTitle, onGenerate, onBack, disabled, userAvatar, userInitial }) {
  const [messages, setMessages] = useState([{ role: "assistant", content: OPENING }]);
  const [chips, setChips] = useState(OPENING_CHIPS);
  const [sections, setSections] = useState([]);
  const [ready, setReady] = useState(false);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [note, setNote] = useState("");
  const threadRef = useRef(null);
  const areaRef = useRef(null);

  // Keep the newest message in view as it streams in.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking, chips]);

  // Auto-grow the composer up to its max height.
  const grow = () => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  };

  const hasContext = messages.some((m) => m.role === "user");

  const sendText = async (raw) => {
    const text = String(raw || "").trim();
    if (!text || thinking) return;
    setNote("");
    setChips([]);
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    if (areaRef.current) areaRef.current.style.height = "auto";
    setThinking(true);

    // Add an empty assistant bubble we stream into.
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const full = await YETI_CHAT(
        { messages: next, title: title || undefined, type: "Playbook" },
        (sofar) => {
          // Markers always trail the reply, so hide anything from the first
          // "[[" while streaming — the real parse happens once complete.
          const shown = sofar.replace(/\[\[[\s\S]*$/, "").trimEnd();
          setMessages((m) => {
            const copy = m.slice();
            copy[copy.length - 1] = { role: "assistant", content: shown };
            return copy;
          });
        }
      );
      const { text: clean, suggestedTitle, chips: nextChips, sections: nextSections, ready: nextReady } = parseMarkers(full);
      setMessages((m) => {
        const copy = m.slice();
        copy[copy.length - 1] = { role: "assistant", content: clean };
        return copy;
      });
      if (suggestedTitle && !title) setTitle(suggestedTitle);
      if (nextChips.length) setChips(nextChips);
      if (nextSections && nextSections.length) setSections(nextSections);
      if (nextReady) setReady(true);
    } catch (e) {
      // Backend brain not reachable — drop the empty bubble and let them keep
      // going. Their answers alone are enough to write from.
      setMessages((m) => {
        const copy = m.slice();
        if (copy[copy.length - 1]?.content === "") copy.pop();
        return copy;
      });
      setNote("YETI couldn't reply just now — you can keep adding detail, then write the playbook whenever you're ready.");
    } finally {
      setThinking(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText(input);
    }
  };

  let hint = "YETI writes using your company's context.";
  if (ready) hint = "YETI has what it needs — write whenever you're ready.";
  else if (hasContext) hint = "Keep adding detail, then write whenever you're ready.";

  const chapterCount = sections.length
    ? `${sections.length} chapter${sections.length === 1 ? "" : "s"}`
    : "";
  let bookFoot = "Contents form as you chat";
  if (ready) bookFoot = `Ready — hit “Write the playbook”${chapterCount ? ` · ${chapterCount}` : ""}`;
  else if (chapterCount) bookFoot = chapterCount;

  return (
    <div className="yc">
      <div className="yc-main">
      <div className="yc-head">
        <Orb size={32} />
        <div className="yc-head-t">
          <div className="yc-title">Generate with YETI</div>
          <div className="yc-sub">Tell YETI what you need — it asks what it needs, then writes.</div>
        </div>
      </div>

      <div className="yc-titlefield">
        <input
          className="yc-titlein"
          placeholder="Playbook title (e.g. Employee manual)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="yc-cap">YETI can name this from your chat — or type your own.</div>
      </div>

      <div className="yc-chat" ref={threadRef}>
        {messages.map((m, i) => (
          // Order is append-only, so index is a stable key here.
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={`yc-msg ${m.role === "user" ? "user" : "yeti"}`}>
            {m.role === "user" ? (
              <span className="yc-mava">
                {/* Initials sit underneath so a missing/404 photo still shows something. */}
                <span className="yc-mava-fallback">{userInitial || "You"}</span>
                {userAvatar && (
                  <img
                    className="yc-mava-img"
                    src={userAvatar}
                    alt="You"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
              </span>
            ) : (
              <Orb size={26} />
            )}
            <div className="yc-bub">
              {m.content
                ? m.content.split("\n").map((line, li) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <p key={li}>{line || " "}</p>
                  ))
                : (
                  <span className="yc-typing" aria-label="YETI is typing">
                    <i /><i /><i />
                  </span>
                )}
            </div>
          </div>
        ))}
      </div>

      {chips.length > 0 && !thinking && (
        <div className="yc-chips">
          {chips.map((c) => (
            <button key={c} type="button" className="yc-chip" onClick={() => sendText(c)} disabled={disabled}>
              <span>{c}</span>
              <svg className="yc-chip-arw" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          ))}
        </div>
      )}

      {ready && !thinking && (
        <div className="yc-ready">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
          <span>YETI has enough to write this — add anything else, or write it now.</span>
        </div>
      )}

      <div className="yc-composer">
        <div className="yc-cbar">
          <textarea
            ref={areaRef}
            className="yc-input"
            placeholder="Message YETI…"
            rows={1}
            value={input}
            onChange={(e) => { setInput(e.target.value); grow(); }}
            onKeyDown={onKeyDown}
            disabled={disabled}
          />
          <button
            type="button"
            className="yc-send"
            onClick={() => sendText(input)}
            disabled={disabled || thinking || !input.trim()}
            aria-label="Send"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2 11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>

        {note && <div className="yc-note">{note}</div>}

        <div className="yc-foot">
          <span className="yc-hint">{note ? " " : hint}</span>
          <div className="yc-actions">
            <button className="pb-btn ghost" onClick={onBack} disabled={disabled}>Back</button>
            <button
              className={`pb-btn primary${ready ? " ready" : ""}`}
              disabled={disabled || !hasContext}
              onClick={() => onGenerate(transcriptOf(messages))}
            >
              <Sparkle size={15} />
              Write the playbook
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* the book taking shape — its contents fill in as YETI proposes them */}
      <div className="yc-side">
        <div className="yc-book">
          <div className="yc-bk-top">
            <span className="yc-bk-cover" aria-hidden="true" />
            <div className="yc-bk-meta">
              <div className={`yc-bk-k${ready ? " done" : ""}`}>{ready ? "Ready to write" : "Building"}</div>
              <div className={`yc-bk-t${title ? "" : " dim"}`}>{title || "Untitled playbook"}</div>
            </div>
          </div>
          {sections.length > 0 ? (
            <div className="yc-bk-list">
              {sections.map((s, i) => (
                // Section order is stable append-only here.
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="yc-bk-row">
                  <span className="yc-bk-n">{i + 1}</span>
                  <span className="yc-bk-rt">{s}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="yc-bk-ghost">
              <span className="yc-bk-ph" aria-hidden="true" />
              <p>Your book&apos;s chapters appear here as you and YETI talk.</p>
            </div>
          )}
          <div className="yc-bk-foot">{bookFoot}</div>
        </div>
      </div>
    </div>
  );
}
